import {
  MANAGEMENT_TIMELINE_DEFAULT_LIMIT,
  parseManagementTimelineQuery,
  serializeManagementTimelineQuery,
  type ManagementTimelineQuery,
} from '@internal/spassu-code-contract';
import type {
  CockpitNavigableState,
  CockpitNavigationPort,
} from '../controller/CockpitDataSource';

const TIMELINE_QUERY_KEYS: Array<keyof ManagementTimelineQuery> = [
  'view',
  'participant',
  'skill',
  'task',
  'outcome',
  'type',
  'from',
  'to',
  'attention',
  'groupBy',
  'groupKey',
  'page',
  'limit',
];
const COCKPIT_QUERY_KEYS = ['revision', ...TIMELINE_QUERY_KEYS];

export function parseCockpitQueryState(
  input: string | URLSearchParams,
): CockpitNavigableState {
  const params = toSearchParams(input);
  const accepted: Record<string, unknown> = {};
  for (const key of TIMELINE_QUERY_KEYS) {
    if (!params.has(key)) continue;
    try {
      const parsed = parseManagementTimelineQuery({
        [key]: params.get(key) ?? undefined,
      });
      if (parsed[key] !== undefined) accepted[key] = parsed[key];
    } catch {
      // URLs são entrada humana: um campo inválido não invalida os demais.
    }
  }
  const timelineQuery = parseSafePeriod(accepted);
  const revision = parsePositiveInteger(params.get('revision'));
  return {
    ...(revision === undefined ? {} : { revision }),
    timelineQuery,
  };
}

export function serializeCockpitQueryState(
  state: Partial<CockpitNavigableState>,
  current: string | URLSearchParams = '',
): URLSearchParams {
  const params = toSearchParams(current);
  COCKPIT_QUERY_KEYS.forEach(key => params.delete(key));
  const normalized = normalizeCockpitNavigableState(state);
  if (normalized.revision !== undefined) {
    params.set('revision', String(normalized.revision));
  }
  const query = serializeManagementTimelineQuery(normalized.timelineQuery);
  Object.entries(query).forEach(([key, value]) => {
    if (
      key !== 'limit' ||
      Number(value) !== MANAGEMENT_TIMELINE_DEFAULT_LIMIT
    ) {
      params.set(key, value);
    }
  });
  return params;
}

export function normalizeCockpitNavigableState(
  state: Partial<CockpitNavigableState> | undefined,
): CockpitNavigableState {
  const revision =
    typeof state?.revision === 'number' &&
    Number.isSafeInteger(state.revision) &&
    state.revision > 0
      ? state.revision
      : undefined;
  return {
    ...(revision === undefined ? {} : { revision }),
    timelineQuery: parseManagementTimelineQuery(state?.timelineQuery ?? {}),
  };
}

export function cockpitNavigableStateKey(
  state: Partial<CockpitNavigableState> | undefined,
): string {
  return serializeCockpitQueryState(state ?? {}).toString();
}

export function createCockpitNavigationPort(
  input: string | URLSearchParams,
  replace: (search: string) => void,
): CockpitNavigationPort {
  const currentSearch = toSearchParams(input).toString();
  const currentState = parseCockpitQueryState(currentSearch);
  return {
    initialState: currentState,
    currentState,
    stateKey: cockpitNavigableStateKey(currentState),
    onChange: state => {
      const next = serializeCockpitQueryState(state, currentSearch).toString();
      if (next !== currentSearch) replace(next);
    },
  };
}

function parseSafePeriod(
  accepted: Record<string, unknown>,
): ManagementTimelineQuery {
  try {
    return parseManagementTimelineQuery(accepted);
  } catch {
    const withoutInvalidPeriod = { ...accepted };
    delete withoutInvalidPeriod.from;
    delete withoutInvalidPeriod.to;
    return parseManagementTimelineQuery(withoutInvalidPeriod);
  }
}

function parsePositiveInteger(value: string | null): number | undefined {
  if (!value || !/^\d+$/.test(value)) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
}

function toSearchParams(input: string | URLSearchParams): URLSearchParams {
  return new URLSearchParams(
    typeof input === 'string' && input.startsWith('?') ? input.slice(1) : input,
  );
}
